import { useEffect, useState } from "react";
import { X, Loader2, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SIZES, priceFor } from "@/lib/pricing";

function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const EMPTY_CUST = { name: "", email: "", phone: "", address: "", postcode: "" };

// Admin-side form to book a customer in while on the phone.
export default function NewBookingModal({ onClose, onCreated }) {
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [size, setSize] = useState("sedan");
  const [vehicle, setVehicle] = useState({ year: "", make: "", model: "" });
  const [customer, setCustomer] = useState(EMPTY_CUST);
  const [date, setDate] = useState(tomorrowIso());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [addonIds, setAddonIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      if (r.data[0]) setServiceId(r.data[0].id);
    }).catch(() => toast.error("Could not load services"));
    api.get("/addons").then((r) => setAddons(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) return;
    api.get("/availability", { params: { date } }).then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
    setTime("");
  }, [date]);

  const service = services.find((s) => s.id === serviceId);
  const servicePrice = priceFor(service, size);
  const addonsTotal = addons.filter((a) => addonIds.includes(a.id)).reduce((s, a) => s + priceFor(a, size), 0);

  const valid =
    serviceId &&
    customer.name.trim() &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customer.email) &&
    customer.phone.trim() &&
    customer.address.trim() &&
    date &&
    time;

  const submit = async () => {
    if (!valid) {
      toast.error("Please fill name, a valid email, phone, address, date and time");
      return;
    }
    setBusy(true);
    let booking;
    try {
      const res = await api.post("/bookings", {
        service_id: serviceId,
        vehicle: { ...vehicle, size },
        customer: { ...customer, email: customer.email.trim().toLowerCase() },
        scheduled_date: date,
        scheduled_time: time,
        terms_accepted: true,
        prerequisite_accepted: true,
        addon_ids: addonIds,
        photo_ids: [],
        notes: notes.trim(),
      });
      booking = res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
      setBusy(false);
      return;
    }
    // Booking exists now — send the deposit link; a failure here must NOT trigger a re-create.
    try {
      await api.post(`/bookings/${booking.id}/send-deposit-link`);
      toast.success(`Booked ${customer.name} in — confirmation + deposit link emailed`);
    } catch (e) {
      toast.warning("Booking created, but the deposit link didn't send. Use 'Resend link' on the booking row.");
    }
    setBusy(false);
    onCreated();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="new-booking-modal">
      <div className="w-full max-w-lg bg-[#0b0f17] border border-white/10 rounded-2xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0b0f17]/95 backdrop-blur-xl border-b border-white/10 flex items-center justify-between p-4 rounded-t-2xl">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff6b00]">Phone booking</div>
            <div className="font-display uppercase font-extrabold text-lg text-slate-100">Book a customer in</div>
          </div>
          <button onClick={onClose} data-testid="new-booking-close" className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="Service">
            <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} data-testid="new-booking-service" className="nb-input">
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Vehicle size">
            <div className="grid grid-cols-2 gap-2">
              {SIZES.map((z) => (
                <button
                  key={z.v}
                  type="button"
                  onClick={() => setSize(z.v)}
                  data-testid={`new-booking-size-${z.v}`}
                  className={`px-3 py-2 rounded-lg border text-sm text-left ${size === z.v ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00] font-bold" : "bg-[#0f1522] text-slate-300 border-white/10"}`}
                >
                  {z.l}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="Year"><input value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} data-testid="new-booking-vehicle-year" className="nb-input" placeholder="2022" /></Field>
            <Field label="Make"><input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} data-testid="new-booking-vehicle-make" className="nb-input" placeholder="Toyota" /></Field>
            <Field label="Model"><input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} data-testid="new-booking-vehicle-model" className="nb-input" placeholder="Camry" /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            <Field label="Customer name"><input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="new-booking-name" className="nb-input" placeholder="Full name" /></Field>
            <Field label="Phone"><input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="new-booking-phone" className="nb-input" placeholder="04xx xxx xxx" /></Field>
          </div>
          <Field label="Email (receives confirmation + deposit link)"><input value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="new-booking-email" className="nb-input" placeholder="name@email.com" /></Field>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Field label="Address"><input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="new-booking-address" className="nb-input" placeholder="Street, suburb" /></Field></div>
            <Field label="Postcode"><input value={customer.postcode} onChange={(e) => setCustomer({ ...customer, postcode: e.target.value })} data-testid="new-booking-postcode" className="nb-input" placeholder="3977" /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            <Field label="Date"><input type="date" min={tomorrowIso()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="new-booking-date" className="nb-input" /></Field>
            <Field label="Time">
              <select value={time} onChange={(e) => setTime(e.target.value)} data-testid="new-booking-time" className="nb-input">
                <option value="">Select a time</option>
                {slots.map((s) => (
                  <option key={s.time} value={s.time} disabled={!s.available}>{s.time}{s.available ? "" : " (taken)"}</option>
                ))}
              </select>
            </Field>
          </div>

          {addons.length > 0 && (
            <Field label="Add-ons (optional)">
              <div className="grid grid-cols-2 gap-2">
                {addons.map((a) => {
                  const on = addonIds.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAddonIds((p) => (on ? p.filter((x) => x !== a.id) : [...p, a.id]))}
                      data-testid={`new-booking-addon-${a.id}`}
                      className={`px-3 py-2 rounded-lg border text-sm text-left ${on ? "bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/40" : "bg-[#0f1522] text-slate-300 border-white/10"}`}
                    >
                      {a.name} <span className="opacity-70">+${priceFor(a, size).toFixed(0)}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <Field label="Notes (optional)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="new-booking-notes" rows={2} className="nb-input" placeholder="Anything CJ should know" />
          </Field>

          <div className="rounded-xl bg-[#0f1522] border border-white/5 p-3 flex items-center justify-between text-sm">
            <span className="text-slate-400">Est. {service?.name} ({SIZES.find((z) => z.v === size)?.l})</span>
            <span className="font-display font-extrabold text-slate-100">${(servicePrice + addonsTotal).toFixed(2)}</span>
          </div>

          <button onClick={submit} disabled={busy} data-testid="new-booking-submit" className="cj-btn-primary w-full px-5 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Create booking & send deposit link
          </button>
          <p className="text-xs text-slate-500 text-center inline-flex items-center justify-center gap-1 w-full">
            <Phone className="h-3 w-3" /> Customer gets a confirmation email plus a one-tap link to pay the deposit.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
